import React, { useEffect, useState } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { UpdateAccountGroupNameModal } from "@/features/accounts/account-group/components/update-account-group-name-modal";
import { KCloudIntegrationModal } from "@/features/accounts/account-group/components/kcloud-integration-modal";
import { ConnectKCloudModal } from "@/features/accounts/account-group/components/connect-kcloud-modal";
import { useGetAccountGroups } from "@/features/accounts/account-group/hooks/use-get-account-groups";
import { useKCloudIntegration, ClientInfo } from "@/features/accounts/account-group/hooks/use-kcloud-integration";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { useTradeJumpContext } from "@/features/trade-jump/contexts/trade-jump-context";
import { SelectMoveAccountGroupModal } from "@/features/accounts/account-group/components/select-move-account-group-modal";
import { MoveAccountGroupModal } from "@/features/accounts/account-group/components/move-account-group-modal";
import { DownloadTradesModal } from "@/features/accounts/shared/components/download-trades-modal";
import { DeleteAccountGroupModal } from "@/features/accounts/account-group/components/delete-account-group-modal";
import { ConnectDisconnectCompleted } from "@/features/my-data/components/connect-disconnect-completed-modal";

interface Props {
  isOwner: boolean;
  onTapCloseModal: () => void;
}

// 口座グループメニューモーダル
export const GroupMenuModal = ({ isOwner, onTapCloseModal }: Props) => {
  const [connectServiceType, setConnectServiceType] = useState<"kaikei" | "shokokai">("kaikei");
  const [connectCompleteType, setConnectCompleteType] = useState<"connect" | "disconnect">("connect");
  const {
    targetAccountGroup,
  } = useAccountGroupsContext();
  const { syncJump } = useTradeJumpContext();
  const {
    getAccountGroups,
  } = useGetAccountGroups();
  const {
    fetchServiceIntegration,
    authenticateAndGetClients,
    updateSelectedClient,
    disconnectKCLService,
    serviceIntegrationInfo,
  } = useKCloudIntegration({ accountGroupId: targetAccountGroup.accountGroupId });
  const {
    openMyDataModal,
    setOpenMyDataModal,
  } = useModalContext();

  useEffect(() => {
    if (openMyDataModal === "ServiceIntegration") {
      fetchServiceIntegration();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMyDataModal]);

  // サービス連携モーダルを開く
  const handleOpenConnectModal = (serviceType: "kaikei" | "shokokai") => {
    setConnectServiceType(serviceType);
    setOpenMyDataModal("ConnectService");
  };

  // サービス連携モーダルを閉じる
  const handleCloseConnectModal = () => {
    setOpenMyDataModal(undefined);
  };

  // サービス連携を実行
  const handleConnectService = async (
    selectedClient: ClientInfo,
  ) => {
    try {
      // 選択された顧問先情報をサーバーに送信して登録、初回連携を行う
      const res = await updateSelectedClient(
        selectedClient,
        targetAccountGroup.accountsList,
        connectServiceType
      );
      // エラー時（error）は処理を中断
      if (res === undefined) {
        return undefined;
      }
      // 登録後、データを再取得するだけ
      await fetchServiceIntegration();
      setConnectCompleteType("connect");
      setOpenMyDataModal("ConnectDisconnectCompleted");
    } catch (error) {
      console.error("サービス連携に失敗しました:", error);
    }
  };

  // 認証とクライアントリスト取得
  const handleAuthenticate = async (kikCode: string, userId: string, password: string): Promise<ClientInfo[]> => {
    const result = await authenticateAndGetClients(kikCode, userId, password, connectServiceType);
    return result ?? [];
  };

  // 連携解除を実行
  const handleDisconnectService = async (serviceType: "kaikei" | "shokokai") => {
    try {
      setConnectServiceType(serviceType);
      await disconnectKCLService();
      await fetchServiceIntegration();
      setConnectCompleteType("disconnect");
      setOpenMyDataModal("ConnectDisconnectCompleted");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      {openMyDataModal === "GroupMenu" && (
        <BasePageModal
          title="口座グループメニュー"
          handleClose={() => {
            onTapCloseModal();
          }}
          className="md-group-menu"
        >
          <h2>
            <strong>{targetAccountGroup.accountGroupsName}</strong>
          </h2>
          <ul>
            {!syncJump && isOwner && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpenMyDataModal("UpdateName")
                }}
                className="btn-edit"
              >
                グループ名の変更
              </button>
            </li>
            )}

            {!syncJump && isOwner && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpenMyDataModal("SelectMoveGroup")
                }}
                className="btn-group"
              >
                グループの移動
              </button>
            </li>
            )}

            {!syncJump && isOwner && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpenMyDataModal("ServiceIntegration")
                }}
                className="btn-joint"
              >
                サービス連携
              </button>
            </li>
            )}

            {!syncJump && isOwner && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpenMyDataModal("Share")
                }}
                className="btn-shear"
              >
                グループの共有
              </button>
            </li>
            )}

            <li>
              <button
                type="button"
                onClick={() => {
                  setOpenMyDataModal("ExcelDownload")
                }}
                className="btn-download"
              >
                明細のダウンロード
              </button>
            </li>

            {!syncJump && isOwner && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setOpenMyDataModal("Delete")
                }}
                className="btn-delete"
              >
                グループの削除
              </button>
            </li>
            )}
          </ul>
        </BasePageModal>
      )}

      {/* グループ名の変更モーダル */}
      {openMyDataModal === "UpdateName" && (
      <UpdateAccountGroupNameModal
        onTapCloseModal={() => {
          onTapCloseModal();
        }}
        onTapCompleteModal={() => {
          getAccountGroups(true);
          onTapCloseModal();
        }}
      />
      )}

      {/* 移動させるグループの選択モーダル */}
      {openMyDataModal === "SelectMoveGroup" && (
      <SelectMoveAccountGroupModal
        onTapCloseModal={() => {
          onTapCloseModal();
        }}
      />
      )}

      {/* グループの移動実行モーダル */}
      {openMyDataModal === "MoveGroup" && (
      <MoveAccountGroupModal
        onTapCloseModal={() => {
          onTapCloseModal();
        }}
        onTapCompleteModal={() => {
          getAccountGroups(true);
          onTapCloseModal();
        }}
      />
      )}

      {/* サービス連携モーダル */}
      <KCloudIntegrationModal
        onTapCloseModal={() => {
          onTapCloseModal();
        }}
        onOpenConnectModal={handleOpenConnectModal}
        serviceIntegrationInfo={serviceIntegrationInfo}
        onDisconnect={handleDisconnectService}
      />

      {/* サービス接続モーダル */}
      {openMyDataModal === "ConnectService" && (
        <ConnectKCloudModal
          onTapCloseModal={() => {
            handleCloseConnectModal();
            onTapCloseModal();
          }}
          serviceType={connectServiceType}
          onConnect={handleConnectService}
          onAuthenticate={handleAuthenticate}
        />
      )}

      {/* まとめてダウンロードモーダル */}
      {openMyDataModal === "ExcelDownload" && (
        <DownloadTradesModal
          downloadAllAtOnce={true}
          onTapCloseModal={() => {
            onTapCloseModal();
          }}
          onTapCompleteModal={() => {
            onTapCloseModal();
          }}
        />
      )}

      {/* グループの削除モーダル */}
      {openMyDataModal === "Delete" && (
        <div className="my-data__delete-account-group-modal">
          <DeleteAccountGroupModal
            onTapCloseModal={() => {
              getAccountGroups(true);
              onTapCloseModal();
            }}
            onTapCompleteModal={() => {
              getAccountGroups(true);
              onTapCloseModal();
            }}
          />
        </div>
      )}

      {/* 連携完了/連携解除完了確認モーダル */}
      {openMyDataModal === "ConnectDisconnectCompleted" && (
        <ConnectDisconnectCompleted
          serviceType={connectServiceType}
          completeType={connectCompleteType}  // "connect" | "disconnect"
          onTapCloseModal={() => {
            setOpenMyDataModal("ServiceIntegration");
          }}
        />
      )}

    </>
  );
};
